import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { defaultSiteSettings, SiteSettings } from "../utils/siteSettings";
import { defaultSiteCopy, SiteCopy } from "../utils/siteCopy";
import { getReliableThumbnailUrl, handleThumbnailError } from "../utils/youtubeThumbnail";
import { isContentVisible } from "../utils/contentVisibility";

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: "hero_video" | "works" | "product" | "pr";
    category_name?: string | null;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string;
    custom_thumbnail: string;
    custom_description: string;
    is_active: boolean | number;
    sort_order: number;
    created_at: string;
}

const SectionStack = styled.div`
    background: #f6f7f4;
    color: #14191f;
`;

const Band = styled.section<{ dark?: boolean }>`
    width: 100%;
    background: ${(props) => (props.dark ? "#11161c" : "transparent")};
    color: ${(props) => (props.dark ? "#f8faf7" : "#14191f")};
`;

const Inner = styled.div`
    width: min(1180px, calc(100% - 40px));
    margin: 0 auto;
    padding: 72px 0;

    @media (max-width: 720px) {
        width: min(100% - 28px, 1180px);
        padding: 48px 0;
    }
`;

const Split = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 0.82fr) minmax(320px, 1.18fr);
    gap: 44px;
    align-items: center;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const Eyebrow = styled.p`
    margin: 0 0 12px;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8b5f2f;
`;

const Heading = styled.h2`
    margin: 0;
    font-size: clamp(2rem, 4vw, 3.6rem);
    line-height: 1.02;
    font-weight: 850;
`;

const Copy = styled.p`
    margin: 18px 0 0;
    color: inherit;
    opacity: 0.78;
    font-size: 1rem;
    line-height: 1.75;
`;

const VideoFrame = styled.a`
    position: relative;
    display: block;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    background: #000;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 8px;
`;

const FrameImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const PlayMark = styled.span`
    position: absolute;
    left: 20px;
    bottom: 18px;
    width: 54px;
    height: 54px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: #f8faf7;
    color: #11161c;
    font-size: 1.45rem;
    line-height: 1;
`;

const WorkGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
    margin-top: 30px;

    @media (max-width: 820px) {
        grid-template-columns: 1fr;
    }
`;

const WorkCard = styled(Link)`
    display: grid;
    gap: 14px;
    color: inherit;
    text-decoration: none;
`;

const ThumbWrap = styled.div`
    overflow: hidden;
    border-radius: 8px;
    aspect-ratio: 16 / 10;
    background: #1d252d;
`;

const WorkTitle = styled.h3`
    margin: 0;
    font-size: 1.05rem;
    line-height: 1.35;
`;

const Meta = styled.p`
    margin: 0;
    color: #637083;
    font-size: 0.9rem;
`;

const StepGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-top: 28px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

const Step = styled.div`
    border-top: 2px solid #1f5f5b;
    padding-top: 14px;
`;

const StepNumber = styled.p`
    margin: 0 0 10px;
    color: #8b5f2f;
    font-weight: 800;
`;

const ServiceGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 14px;
    margin-top: 28px;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const Service = styled.div`
    border: 1px solid #d7ddd5;
    border-radius: 8px;
    padding: 18px;
    background: rgba(255, 255, 255, 0.62);
`;

const PanelGrid = styled.div`
    display: grid;
    gap: 14px;
`;

const FieldList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
`;

const FieldPill = styled.span`
    display: inline-flex;
    min-height: 32px;
    align-items: center;
    padding: 0 12px;
    border-radius: 999px;
    background: #11161c;
    color: #f8faf7;
    font-size: 0.86rem;
    font-weight: 800;
`;

const Cta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;

    @media (max-width: 720px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const CtaLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 46px;
    padding: 0 18px;
    border-radius: 8px;
    background: #f8faf7;
    color: #11161c;
    font-weight: 800;
    text-decoration: none;
`;

const steps = ["상담", "기획", "촬영", "편집", "납품"];
const services = [
    ["Brand Film", "브랜드 스토리와 메시지를 선명하게 정리하는 홍보 영상"],
    ["Product Video", "제품의 질감, 사용 맥락, 구매 이유를 보여주는 제품 영상"],
    ["Short-form", "광고 소재와 SNS 운영에 맞춘 숏폼 및 세로형 콘텐츠"],
];
const clientFields = ["Beauty", "Fashion", "F&B", "Startup", "Corporate PR", "Creator"];
const testimonials = [
    ["기획 의도를 빠르게 이해하고 브랜드 톤에 맞춰 완성도를 끌어올립니다.", "Brand Project"],
    ["촬영과 편집의 우선순위가 명확해 일정 안에서 결과물을 안정적으로 만듭니다.", "Production Partner"],
];

const PortfolioSections: React.FC = () => {
    const [settings, setSettings] = useState<SiteSettings>(defaultSiteSettings);
    const [copy, setCopy] = useState<SiteCopy>(defaultSiteCopy);
    const [content, setContent] = useState<Content[]>([]);

    useEffect(() => {
        const load = async () => {
            const [settingsRes, copyRes, contentRes] = await Promise.all([
                axios.get("/api/site-settings").catch(() => ({ data: { data: defaultSiteSettings } })),
                axios.get("/api/site-copy").catch(() => ({ data: { data: defaultSiteCopy } })),
                axios.get("/api/content").catch(() => ({ data: { data: [] } })),
            ]);
            setSettings({ ...defaultSiteSettings, ...(settingsRes.data.data || {}) });
            setCopy({ ...defaultSiteCopy, ...(copyRes.data.data || {}) });
            setContent((contentRes.data.data || []).filter(isContentVisible));
        };

        load();
    }, []);

    const featured = useMemo(
        () => content.filter((item) => item.content_type !== "hero_video").slice(0, 3),
        [content]
    );
    const showreel = useMemo(
        () => content.find((item) => item.content_type === "hero_video") || featured[0],
        [content, featured]
    );

    if (!Object.values(settings).some(Boolean)) return null;

    return (
        <SectionStack>
            {settings.show_showreel && showreel && (
                <Band dark>
                    <Inner>
                        <Split>
                            <div>
                                <Eyebrow>Showreel</Eyebrow>
                                <Heading>{copy.showreel_heading}</Heading>
                                <Copy>{showreel.custom_description || showreel.description || copy.showreel_body}</Copy>
                            </div>
                            <VideoFrame href={showreel.youtube_url} target="_blank" rel="noopener noreferrer" aria-label={`${showreel.title} 보기`}>
                                <FrameImage src={getReliableThumbnailUrl(showreel.custom_thumbnail || showreel.thumbnail_url)} alt={showreel.title} onError={(event) => handleThumbnailError(event.currentTarget)} />
                                <PlayMark aria-hidden="true">▶</PlayMark>
                            </VideoFrame>
                        </Split>
                    </Inner>
                </Band>
            )}

            {settings.show_featured_work && featured.length > 0 && (
                <Band>
                    <Inner>
                        <Eyebrow>Selected Work</Eyebrow>
                        <Heading>{copy.featured_heading}</Heading>
                        <WorkGrid>
                            {featured.map((item) => (
                                <WorkCard key={item.id} to={`/works/detail/${item.id}`}>
                                    <ThumbWrap>
                                        <FrameImage src={getReliableThumbnailUrl(item.custom_thumbnail || item.thumbnail_url)} alt={item.title} onError={(event) => handleThumbnailError(event.currentTarget)} />
                                    </ThumbWrap>
                                    <div>
                                        <WorkTitle>{item.title}</WorkTitle>
                                        <Meta>{item.category_name || "Video Portfolio"}</Meta>
                                    </div>
                                </WorkCard>
                            ))}
                        </WorkGrid>
                    </Inner>
                </Band>
            )}

            {settings.show_process && (
                <Band>
                    <Inner>
                        <Eyebrow>Process</Eyebrow>
                        <Heading>{copy.process_heading}</Heading>
                        <StepGrid>
                            {steps.map((step, index) => (
                                <Step key={step}>
                                    <StepNumber>{String(index + 1).padStart(2, "0")}</StepNumber>
                                    <WorkTitle>{step}</WorkTitle>
                                </Step>
                            ))}
                        </StepGrid>
                    </Inner>
                </Band>
            )}

            {settings.show_services && (
                <Band>
                    <Inner>
                        <Eyebrow>Capability</Eyebrow>
                        <Heading>{copy.services_heading}</Heading>
                        <ServiceGrid>
                            {services.map(([title, description]) => (
                                <Service key={title}>
                                    <WorkTitle>{title}</WorkTitle>
                                    <Copy>{description}</Copy>
                                </Service>
                            ))}
                        </ServiceGrid>
                    </Inner>
                </Band>
            )}

            {settings.show_clients && (
                <Band>
                    <Inner>
                        <Split>
                            <div>
                                <Eyebrow>Clients & Feedback</Eyebrow>
                                <Heading>{copy.clients_heading}</Heading>
                                <Copy>{copy.clients_body}</Copy>
                            </div>
                            <PanelGrid>
                                <Service>
                                    <WorkTitle>작업 분야</WorkTitle>
                                    <FieldList>
                                        {clientFields.map((field) => <FieldPill key={field}>{field}</FieldPill>)}
                                    </FieldList>
                                </Service>
                                {testimonials.map(([quote, source]) => (
                                    <Service key={source}>
                                        <WorkTitle>{source}</WorkTitle>
                                        <Copy>{quote}</Copy>
                                    </Service>
                                ))}
                            </PanelGrid>
                        </Split>
                    </Inner>
                </Band>
            )}

            {settings.show_contact_cta && (
                <Band dark>
                    <Inner>
                        <Cta>
                            <div>
                                <Eyebrow>Project Inquiry</Eyebrow>
                                <Heading>{copy.contact_cta_heading}</Heading>
                            </div>
                            <CtaLink to="/contacts">{copy.contact_button_label}</CtaLink>
                        </Cta>
                    </Inner>
                </Band>
            )}
        </SectionStack>
    );
};

export default PortfolioSections;
